import './App.css';
import ClientesV2 from './components/ClientesV2';
import PortalRegistro from './portal/PortalRegistro';
import PortalVerificar from './portal/PortalVerificar';
import PortalPlan from './portal/PortalPlan';
import PortalContrato from './portal/PortalContrato';
import PortalPago from './portal/PortalPago';
import PortalActivacion from './portal/PortalActivacion';
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Login from './components/Login';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import CDRPage from './components/CDRPage';
import Settings from './components/Settings';
import Carriers from './components/Carriers';
import Planes from './components/Planes';
import DIDSeries from './components/DIDSeries';
import Metricas from './components/Metricas';
import NetworkMap from './components/NetworkMap';
import AuditLog from './components/AuditLog';
import Softphone from './components/Softphone';
import Usuarios from './components/Usuarios';
import Antifraude from './components/Antifraude';
import APICenter from './components/APICenter';
import Revendedores from './components/Revendedores';
import Billing from './components/Billing';
import Recaudacion from './components/Recaudacion';
import MobileApp from './components/MobileApp';
import CDRLive from './components/CDRLive';
import CarrierMonitor from './components/CarrierMonitor';
import ResellerDashboard from './components/ResellerDashboard';
import Alerts from './components/Alerts';
import ReportesARCOTEL from './components/ReportesARCOTEL';
import Routing from './components/Routing';
import Permissions from './components/Permissions';
import HistorialANI from './components/HistorialANI';
import UFWManager from './components/UFWManager';
import SupportBubble from './components/SupportBubble';
import KamaIoSBC from './components/KamaIoSBC';
import DiscadoCorto from './components/DiscadoCorto';
import { AppFooter } from './version';
import { surfaceFromHost, SURFACE_DEFAULTS } from './surface';

// Host-aware: cada subdominio es una "superficie" dedicada (migración a contenedores).
const HOST = typeof window !== "undefined" ? window.location.hostname : "";
const SURFACE = surfaceFromHost(HOST);
const SURFACE_DEFAULT = SURFACE_DEFAULTS[SURFACE] || "/dashboard";
function pathPermitido(path) {
  if (SURFACE === "recaudacion") return path.startsWith("/recaudacion");
  if (SURFACE === "billing")     return path.startsWith("/billing");
  if (SURFACE === "clientes")    return path.startsWith("/clientes-v2");
  // panel (mi.eneural.org): todo MENOS recaudación/billing (viven en sus subdominios)
  return !path.startsWith("/recaudacion") && !path.startsWith("/billing");
}

// Redirige a la superficie del host: en un subdominio '/' -> su módulo; y cualquier
// ruta no permitida para el host -> su default (así mi.eneural.org bloquea /recaudacion
// y /billing, y los subdominios no dejan navegar al resto del panel).
function HostGuard() {
  const loc = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    const p = loc.pathname;
    if (p === "/" && SURFACE !== "panel") { nav(SURFACE_DEFAULT, { replace: true }); return; }
    if (!pathPermitido(p)) nav(SURFACE_DEFAULT, { replace: true });
  }, [loc.pathname, nav]);
  return null;
}

function Layout({ children }) {
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="app-main">
        <Navbar />
        <main className="page-content">
          <HostGuard />
          {children}
        </main>
        <AppFooter />
      </div>
      {SURFACE === "panel" && <SupportBubble />}
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/registro"           element={<PortalRegistro />} />
          <Route path="/verificar"          element={<PortalVerificar />} />
          <Route path="/portal/plan"        element={<PortalPlan />} />
          <Route path="/portal/contrato"    element={<PortalContrato />} />
          <Route path="/portal/pago"        element={<PortalPago />} />
          <Route path="/portal/activacion"  element={<PortalActivacion />} />
          <Route path="/login"              element={<Login />} />
          <Route path="/*" element={
            <PrivateRoute>
              <Layout>
                <Routes>
                  <Route path="/kamailio-sbc"       element={<KamaIoSBC />} />
                  <Route path="/kamailio-sbc/:tab"  element={<KamaIoSBC />} />
                  <Route path="/"              element={<Dashboard />} />
                  <Route path="/dashboard"     element={<Dashboard />} />
                  <Route path="/cdr"           element={<CDRPage />} />
                  <Route path="/settings"      element={<Settings />} />
                  <Route path="/carriers"      element={<Carriers />} />
                  <Route path="/planes"        element={<Planes />} />
                  <Route path="/clientes-v2"   element={<ClientesV2 />} />
                  <Route path="/dids"          element={<DIDSeries />} />
                  <Route path="/discado-corto" element={<DiscadoCorto />} />
                  <Route path="/did-series"    element={<DIDSeries />} />
                  <Route path="/metricas"      element={<Metricas />} />
                  <Route path="/network"       element={<NetworkMap />} />
                  <Route path="/auditoria"     element={<AuditLog />} />
                  <Route path="/softphone"     element={<Softphone />} />
                  <Route path="/usuarios"      element={<Usuarios />} />
                  <Route path="/antifraude"    element={<Antifraude />} />
                  <Route path="/api-center"   element={<APICenter />} />
                  <Route path="/revendedores"  element={<Revendedores />} />
                  <Route path="/billing"       element={<Billing />} />
                  <Route path="/recaudacion"   element={<Recaudacion />} />
                  <Route path="/app"          element={<MobileApp />} />
                  <Route path="/cdr-live"     element={<CDRLive />} />
                  <Route path="/carrier-monitor" element={<CarrierMonitor />} />
                  <Route path="/alerts"            element={<Alerts />} />
                  <Route path="/routing"           element={<Routing />} />
                  <Route path="/permisos"          element={<Permissions />} />
                  <Route path="/historial-ani"     element={<HistorialANI />} />
                  <Route path="/ufw"                element={<UFWManager />} />
                  <Route path="/arcotel"           element={<ReportesARCOTEL />} />
                  <Route path="/reseller-dashboard" element={<ResellerDashboard />} />
                  <Route path="/reseller-cdrs"      element={<CDRPage />} />
                  <Route path="/reseller-billing"   element={<Billing />} />
                </Routes>
              </Layout>
            </PrivateRoute>
          } />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}
